import { safeRequestFailure } from './dnmFailure';
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '../config/env';
import {
  getIdToken,
  hasSession,
  invalidateIdToken,
  redirectToLogin,
  signOut,
} from '../auth/cognito';

/**
 * Axios instance for rhino-api (mirrors the rhino_core Dio pattern):
 *  - injects the Cognito ID TOKEN (admin-pool tokens must be ID tokens —
 *    rhino-api validates token_use == "id" and the email domain)
 *  - getIdToken() refreshes transparently via the hosted-UI token endpoint
 *  - a 401 first forces one token refresh and retries the request once (a
 *    request can reach the API with a token that expired in flight, e.g.
 *    after a laptop sleep); only a 401 on the retry, or a refresh token
 *    Cognito rejects, ends the session
 */

type RetriableConfig = InternalAxiosRequestConfig & { _retriedAfter401?: boolean };

export const api = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (config) => {
  const token = await getIdToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function endSession(): void {
  signOut();
  redirectToLogin();
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: string }>) => {
    if (error.response?.status !== 401) return Promise.reject(safeRequestFailure(error));

    const config = error.config as RetriableConfig | undefined;
    if (!config || config._retriedAfter401) {
      // Second 401 in a row with a freshly refreshed token: the session is dead.
      endSession();
      return Promise.reject(safeRequestFailure(error));
    }

    config._retriedAfter401 = true;
    invalidateIdToken();
    const token = await getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      return api.request(config);
    }
    // No token: either the refresh token was rejected (session already
    // cleared) or the token endpoint was unreachable (keep the session, fail
    // this request only).
    if (!hasSession()) redirectToLogin();
    return Promise.reject(safeRequestFailure(error));
  },
);
